import { ClientProps } from '@/types';

export interface SelectedBrandPairLike {
  codLoja?: number | string | null;
  codSegmento?: number | string | null;
}

/** Lê `codLoja`/`codSegmento` de um objeto (ex.: `selected_brand` no storage) para usar em `buildScopedClient`. */
export const parseBrandPairFromStorage = (value: unknown): SelectedBrandPairLike | null => {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const codLoja = Number(raw.codLoja);
  const codSegmento = Number(raw.codSegmento);
  if (!Number.isFinite(codLoja) || !Number.isFinite(codSegmento)) return null;
  return { codLoja, codSegmento };
};

export const getSegmentCode = (segmento: any): number =>
  Number(segmento?.cod_segmento ?? segmento?.COD_SEGMENTO ?? segmento);

export const isBiFolderSegment = (nro: number | null | undefined): boolean => {
  const n = Number(nro);
  return n === 60 || n === 61 || n === 998 || n === 999;
};

export const findLojaForBrandPair = (
  selectedClient: ClientProps | null | undefined,
  selectedBrandPair: SelectedBrandPairLike | null | undefined
) => {
  const lojas = Array.isArray(selectedClient?.lojas) ? selectedClient.lojas : [];
  const codLoja = Number(selectedBrandPair?.codLoja);
  const codSegmento = Number(selectedBrandPair?.codSegmento);
  if (!Number.isFinite(codLoja) || !lojas.length) return null;

  const matched = lojas.find((loja: any) => {
    if (Number(loja?.COD_LOJA) !== codLoja) return false;
    const segmentos = Array.isArray(loja?.COD_SEGMENTO) ? loja.COD_SEGMENTO : [];
    return segmentos.some((s: any) => getSegmentCode(s) === codSegmento);
  });
  if (matched) return matched;

  if (isBiFolderSegment(codSegmento)) {
    return lojas.find((loja: any) => Number(loja?.COD_LOJA) === codLoja) || null;
  }

  return null;
};

/**
 * Re-escopa o `selectedClient` para a loja+segmento ativos de `selectedBrandPair`,
 * validando contra `selectedClient.lojas`.
 *
 * Regras (em ordem):
 * 1. Se não houver brand pair ativo, devolve o `selectedClient` original.
 * 2. Se o pair bate com uma loja em `selectedClient.lojas`, devolve o cliente
 *    com `COD_LOJA`/`COD_TABELA_VENDA`/etc. da loja matched e
 *    `COD_SEGMENTO = [segmentoAtivo]`.
 * 3. Fallback legado: cliente sem o array `lojas` hidratado — só aceita se o
 *    `COD_LOJA`/`COD_SEGMENTO` raiz do cliente bater com o pair.
 * 4. Caso contrário (cliente tem `lojas`, mas o pair não está em nenhuma delas):
 *    retorna `null`. Os consumidores devem tratar como "combinação inválida"
 *    e evitar carregar/calcular dados da loja errada silenciosamente.
 */
export const buildScopedClient = (
  selectedClient: ClientProps | null | undefined,
  selectedBrandPair: SelectedBrandPairLike | null | undefined
): ClientProps | null => {
  if (!selectedClient) return null;
  if (!selectedBrandPair?.codLoja || !selectedBrandPair?.codSegmento) {
    return selectedClient as ClientProps;
  }

  const codLojaAtiva = Number(selectedBrandPair.codLoja);
  const codSegmentoAtivo = Number(selectedBrandPair.codSegmento);
  if (!Number.isFinite(codLojaAtiva) || !Number.isFinite(codSegmentoAtivo)) {
    return selectedClient as ClientProps;
  }

  const lojas = Array.isArray(selectedClient.lojas) ? selectedClient.lojas : [];
  const matchedLoja = findLojaForBrandPair(selectedClient, selectedBrandPair);

  if (matchedLoja) {
    const segmentosLoja = Array.isArray(matchedLoja?.COD_SEGMENTO) ? matchedLoja.COD_SEGMENTO : [];
    const segmentoAtivo = segmentosLoja.find((s: any) => getSegmentCode(s) === codSegmentoAtivo);
    const isBiFolder = isBiFolderSegment(codSegmentoAtivo) || isBiFolderSegment(getSegmentCode(segmentoAtivo));
    const payLoja = matchedLoja?.COD_PAGAMENTO;
    const payCliente = selectedClient?.COD_PAGAMENTO;
    const COD_PAGAMENTO =
      Array.isArray(payLoja) && payLoja.length > 0
        ? payLoja
        : Array.isArray(payCliente) && payCliente.length > 0
          ? payCliente
          : Array.isArray(payLoja)
            ? payLoja
            : Array.isArray(payCliente)
              ? payCliente
              : [];

    return {
      ...selectedClient,
      ...matchedLoja,
      COD_SEGMENTO: isBiFolder ? segmentosLoja : segmentoAtivo ? [segmentoAtivo] : segmentosLoja,
      COD_PAGAMENTO,
    } as ClientProps;
  }

  if (lojas.length === 0) {
    if (Number(selectedClient.COD_LOJA) !== codLojaAtiva) return null;
    const segmentosCliente = Array.isArray(selectedClient.COD_SEGMENTO) ? selectedClient.COD_SEGMENTO : [];
    if (isBiFolderSegment(codSegmentoAtivo)) {
      return {
        ...selectedClient,
        COD_SEGMENTO: segmentosCliente.length ? segmentosCliente : selectedClient.COD_SEGMENTO,
      } as ClientProps;
    }
    const segmentoAtivo = segmentosCliente.find((s: any) => getSegmentCode(s) === codSegmentoAtivo);
    if (!segmentoAtivo) return null;

    return {
      ...selectedClient,
      COD_SEGMENTO: [segmentoAtivo],
    } as ClientProps;
  }

  return null;
};
